import { useState, useRef, useEffect } from 'react';
import { Send, AlertTriangle, Megaphone, X, ChevronDown, ChevronUp, Search, ArrowLeft, Image, Mic, Square } from 'lucide-react';
import { useStore } from '../store/useStore';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { BROADCAST_LABELS } from './MessagesView';
import { compressImage } from '../lib/imageCompression';
import { useMessages } from '../hooks/useMessages';

export function MessagingDrawer() {
  const { addMessage, addBroadcastMessage, messagingDrawer, setMessagingDrawerOpen, openReply } = useStore();
  const { team: teamList, categories } = useVisibleStore();

  // ── Shared message logic (same hook as MessagesView) ──
  const {
    filteredTeam, sortedTeam,
    getContactPreview, getActiveMessages, getSenderLabel,
    isAdmin, isManager, isEmailValid,
  } = useMessages();

  const chatScrollRef = useRef<HTMLDivElement>(null);

  const { isOpen, selectedTeamId } = messagingDrawer;
  const setSelectedTeamId = (id: string) => openReply(id);

  // Auto-scroll chat to bottom when conversation changes
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [selectedTeamId]);
  
  // Collapsible broadcast & search states
  const [isBroadcastCollapsed, setIsBroadcastCollapsed] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Direct message state
  const [content, setContent] = useState('');
  const [attachedImage, setAttachedImage] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);



  // Broadcast state
  const [broadcastType, setBroadcastType] = useState<'alert' | 'note'>('alert');
  const [broadcastCategoryId, setBroadcastCategoryId] = useState<string>('all');
  const [broadcastContent, setBroadcastContent] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastSent, setBroadcastSent] = useState(false);

  const handleSend = () => {
    if (!selectedTeamId) return;
    const hasC = !!content.trim(); const hasI = !!attachedImage; const hasV = !!audioBlob;
    if (!hasC && !hasI && !hasV) return;
    if (hasV && audioBlob) {
      const r = new FileReader();
      r.onloadend = () => { addMessage(selectedTeamId, content.trim()||'🎤', getSenderLabel(), null, r.result as string); setContent(''); setAudioBlob(null); };
      r.readAsDataURL(audioBlob);
    } else if (hasI) {
      addMessage(selectedTeamId, content.trim()||'📷', getSenderLabel(), attachedImage, null);
      setContent(''); setAttachedImage(null);
    } else { addMessage(selectedTeamId, content.trim(), getSenderLabel()); setContent(''); }
  };
  const startRec = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(s, { mimeType: 'audio/webm;codecs=opus' });
      (window as any).__mr = rec;
      const chunks: Blob[] = [];
      rec.ondataavailable = e => chunks.push(e.data);
      rec.onstop = () => { setAudioBlob(new Blob(chunks, { type: 'audio/webm' })); s.getTracks().forEach(t => t.stop()); };
      rec.start(); setRecording(true);
    } catch { alert('Microphone inaccessible.'); }
  };
  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    setAttachedImage(await compressImage(f, 400, 400, 0.5));
  };

  const getBroadcastTargets = (): string[] => {
    let targets = teamList;
    if (broadcastCategoryId !== 'all') {
      targets = targets.filter(s => s.categoryId === broadcastCategoryId);
    }
    return targets.map(s => s.id);
  };

  const handleBroadcast = () => {
    if (!broadcastContent.trim()) return;
    const teamIds = getBroadcastTargets();
    if (teamIds.length === 0) return;
    setBroadcastSending(true);
    addBroadcastMessage(teamIds, broadcastContent.trim(), BROADCAST_LABELS[broadcastType as keyof typeof BROADCAST_LABELS]);
    setTimeout(() => {
      setBroadcastSending(false);
      setBroadcastSent(true);
      setBroadcastContent('');
      setTimeout(() => setBroadcastSent(false), 3000);
    }, 500);
  };

  const broadcastCount = getBroadcastTargets().length;

  if (!isAdmin && !isManager) return null;

  return (
    <>
      {/* Drawer Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70] transition-opacity"
          onClick={() => setMessagingDrawerOpen(false)}
        />
      )}

      {/* Drawer Content */}
      <div className={`fixed inset-y-0 right-0 w-full max-w-sm bg-[#14161B] border-l border-[#2D3139] z-[80] transform transition-transform duration-500 ease-out shadow-2xl flex flex-col ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="p-6 border-b border-[#2D3139] flex items-center justify-between bg-[#1A1D23]">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-indigo-400" />
              Quick Messaging
            </h2>
            <p className="text-xs text-slate-400 mt-1">Envoyez des alertes ou messages directs</p>
          </div>
          <button 
            onClick={() => setMessagingDrawerOpen(false)}
            className="p-2 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">

          {/* ─── Message Collectif (Collapsible) ─── */}
          <div className="bg-[#0A0B0E]/50 border border-red-500/20 rounded-2xl p-5 space-y-4">
            <button 
              onClick={() => setIsBroadcastCollapsed(!isBroadcastCollapsed)}
              className="w-full flex items-center gap-2 text-left focus:outline-none"
            >
              <Megaphone className="w-5 h-5 text-red-400 animate-pulse" />
              <h3 className="font-bold text-white text-sm">Message Collectif</h3>
              <span className="text-[10px] bg-red-500/10 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-full font-extrabold ml-2">
                {broadcastCount} cibles
              </span>
              <span className="ml-auto text-slate-400">
                {isBroadcastCollapsed ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronUp className="w-4 h-4" />
                )}
              </span>
            </button>

            {!isBroadcastCollapsed && (
              <div className="space-y-4 pt-2 border-t border-[#2D3139]/30">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setBroadcastType('alert')}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border text-[11px] font-bold transition-all ${
                      broadcastType === 'alert'
                        ? 'bg-red-500/20 border-red-500/50 text-red-400'
                        : 'bg-[#1A1D23] border-[#2D3139] text-slate-400 hover:text-red-400'
                    }`}
                  >
                    <AlertTriangle className="w-3.5 h-3.5" /> Alerte
                  </button>
                  <button
                    onClick={() => setBroadcastType('note')}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-xl border text-[11px] font-bold transition-all ${
                      broadcastType === 'note'
                        ? 'bg-sky-500/20 border-sky-500/50 text-sky-400'
                        : 'bg-[#1A1D23] border-[#2D3139] text-slate-400 hover:text-sky-400'
                    }`}
                  >
                    <Megaphone className="w-3.5 h-3.5" /> Note
                  </button>
                </div>

                {isAdmin && (
                  <select
                    value={broadcastCategoryId}
                    onChange={e => setBroadcastCategoryId(e.target.value)}
                    className="w-full bg-[#1A1D23] border border-[#2D3139] rounded-xl px-4 py-2.5 text-white text-sm focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="all">🌐 Toutes les catégories</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}

                <textarea
                  value={broadcastContent}
                  onChange={e => setBroadcastContent(e.target.value)}
                  placeholder={broadcastType === 'alert' ? 'Message d\'alerte urgente...' : 'Note générale...'}
                  rows={3}
                  className={`w-full bg-[#1A1D23] border rounded-xl px-4 py-3 text-white text-sm placeholder-slate-600 focus:outline-none resize-none transition-colors ${
                    broadcastType === 'alert' ? 'border-red-500/30 focus:border-red-500' : 'border-sky-500/30 focus:border-sky-500'
                  }`}
                />

                <button
                  onClick={handleBroadcast}
                  disabled={!broadcastContent.trim() || broadcastSending || broadcastCount === 0}
                  className={`w-full py-3 font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-40 ${
                    broadcastSent ? 'bg-emerald-600 text-white' : broadcastType === 'alert' ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-sky-600 hover:bg-sky-500 text-white'
                  }`}
                >
                  {broadcastSent ? '✅ Envoyé' : <><Send className="w-4 h-4" /> Envoyer à {broadcastCount}</>}
                </button>
              </div>
            )}
          </div>

          {/* ─── WhatsApp Style Contacts List (shown when broadcast is collapsed) ─── */}
          {isBroadcastCollapsed && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Discussions récentes</span>
                <span className="text-[10px] text-indigo-400 font-bold">{filteredTeam.length} contacts</span>
              </div>

              {/* Search box */}
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Rechercher un contact..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full bg-[#1A1D23] border border-[#2D3139]/80 rounded-xl pl-10 pr-4 py-2.5 text-white text-xs placeholder-slate-600 focus:border-indigo-500 focus:outline-none"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Contacts list - sorted by last message time */}
              <div className="space-y-2 pr-1">
                {filteredTeam.length === 0 ? (
                  <p className="text-center text-slate-500 text-xs py-8 italic">Aucun contact trouvé</p>
                ) : (
                  sortedTeam.filter(s => {
                    if (!searchQuery.trim()) return true;
                    const q = searchQuery.toLowerCase();
                    const cat = categories.find(c => c.id === s.categoryId);
                    return s.name.toLowerCase().includes(q) || (cat?.name || '').toLowerCase().includes(q);
                  }).map(s => {
                    const { lastMsg, hasUnread } = getContactPreview(s.id, s.name);
                    const cat = categories.find(c => c.id === s.categoryId);

                    const emailOk = isEmailValid(s);
                    return (
                      <button
                        key={s.id}
                        onClick={() => emailOk ? setSelectedTeamId(s.id) : null}
                        disabled={!emailOk}
                        title={!emailOk ? 'Email non validé — Contact en lecture seule' : undefined}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                          !emailOk
                            ? 'opacity-50 cursor-not-allowed bg-[#0A0B0E]/20 border-[#2D3139]/20'
                            : selectedTeamId === s.id
                            ? 'bg-indigo-600/10 border-indigo-500/40'
                            : 'bg-[#0A0B0E]/30 border-[#2D3139]/30 hover:bg-[#1A1D23]/60 hover:border-[#2D3139]'
                        }`}
                      >
                        {/* Avatar with orange blinking dot for unread */}
                        <div className="relative">
                          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 text-indigo-300 flex items-center justify-center font-bold text-xs">
                            {s.name.substring(0, 2).toUpperCase()}
                          </div>
                          {hasUnread && (
                            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full animate-orange-blink border border-[#14161B]" />
                          )}
                        </div>

                        {/* Text fields */}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-baseline">
                            <span className="font-bold text-white text-xs truncate">{s.name}</span>
                            {lastMsg && (
                              <span className="text-[9px] text-slate-500 shrink-0 font-medium">
                                {new Date(lastMsg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-[9px] text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-1.5 py-0.2 rounded font-extrabold shrink-0">
                              {cat?.name || 'Général'}
                            </span>
                            {s.isArtist && (
                              <span className="text-[9px] text-amber-400 bg-amber-400/10 border border-amber-400/30 px-1.5 py-0.2 rounded font-extrabold shrink-0" title="Artiste">👑 Artiste</span>
                            )}
                            {!emailOk && (
                              <span className="text-[8px] text-amber-500 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.2 rounded font-bold shrink-0">
                                Mail non validé
                              </span>
                            )}
                            {lastMsg ? (
                              <p className="text-[10px] text-slate-500 truncate flex-1 font-medium pl-1">
                                {lastMsg.fromTeam ? '📥 ' : '📤 '}
                                {lastMsg.content}
                              </p>
                            ) : (
                              <p className="text-[10px] text-slate-600 italic truncate flex-1 pl-1">Pas encore de message</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="p-4 bg-[#1A1D23] border-t border-[#2D3139] text-center shrink-0">
          <p className="text-[10px] text-slate-500 italic">Les messages sont envoyés via Push Notifications FCM</p>
        </div>

        {/* ─── Premium Slide-in Chat View (Superposed on top of the list) ─── */}
        {selectedTeamId && (() => {
          const activeTeam = teamList.find(s => s.id === selectedTeamId);
          if (!activeTeam) return null;
          const teamMessages = getActiveMessages(selectedTeamId, activeTeam.name);

          return (
            <div 
              className="absolute inset-0 bg-[#14161B] z-[85] flex flex-col"
            >
              {/* Header with Back button */}
              <div className="p-6 border-b border-[#2D3139] flex items-center gap-3 bg-[#1A1D23] shrink-0">
                <button
                  onClick={() => setSelectedTeamId('')}
                  className="p-2 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white transition-colors"
                  title="Retour"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 text-indigo-300 flex items-center justify-center font-bold text-xs shrink-0">
                    {activeTeam.name.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-bold text-white text-xs truncate">{activeTeam.name}</h4>
                    <p className="text-[9px] text-indigo-400 font-extrabold uppercase tracking-widest mt-0.5">Message Direct</p>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedTeamId('')}
                  className="p-2 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white transition-colors shrink-0"
                  title="Fermer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* History Area */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4 bg-[#0A0B0E]/40 min-h-0" ref={chatScrollRef}>
                {teamMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-500 space-y-3">
                    <div className="w-12 h-12 rounded-full bg-slate-900 border border-[#2D3139] flex items-center justify-center">
                      <Send className="w-5 h-5 text-slate-600" />
                    </div>
                    <p className="text-center text-xs italic">Aucun message échangé. Écrivez un message ci-dessous pour démarrer la discussion.</p>
                  </div>
                ) : (
                  teamMessages.map((m) => (
                    <div
                      key={m.id}
                      className={`flex flex-col ${
                        m.fromTeam ? 'mr-auto items-start' : 'ml-auto items-end'
                      }`}
                    >
                      <div
                        className={`rounded-2xl px-4 py-2.5 text-xs leading-relaxed font-medium max-w-[80%] ${
                          m.fromTeam
                            ? 'bg-[#1C1F26] border border-[#2D3139]/60 text-slate-200 rounded-tl-sm'
                            : 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/10 rounded-tr-sm'
                        }`}
                      >
                        {m.content}
                        {m.imageUrl && <img src={m.imageUrl} className="mt-2 max-w-[160px] max-h-[160px] rounded-lg border border-white/10 cursor-pointer" alt="img" onClick={() => window.open(m.imageUrl!, '_blank')} />}
                        {m.voiceUrl && <audio src={m.voiceUrl} controls className="mt-2 h-7 max-w-[160px]" preload="metadata" />}
                      </div>
                      <span className="text-[9px] text-slate-500 mt-1.5 px-1 truncate max-w-full block">
                        {m.senderName || (m.fromTeam ? activeTeam.name : 'Vous')} • {new Date(m.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* Input Area */}
              <div className="p-6 bg-[#14161B] border-t border-[#2D3139]/50 space-y-3 shrink-0 pb-safe">
                {attachedImage && (
                  <div className="relative inline-block"><img src={attachedImage} className="h-16 rounded-lg border border-[#2D3139]" alt="preview" /><button onClick={() => setAttachedImage(null)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"><X className="w-2.5 h-2.5 text-white" /></button></div>
                )}
                {audioBlob && (
                  <div className="flex items-center gap-2 bg-[#1A1D23] rounded-lg px-2 py-1"><span className="text-[10px] text-emerald-400">🎤 Prêt</span><audio src={URL.createObjectURL(audioBlob)} controls className="h-6 w-24" /><button onClick={() => setAudioBlob(null)} className="text-red-400"><X className="w-3 h-3" /></button></div>
                )}
                <div className="flex gap-3">
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
                  <button onClick={() => fileInputRef.current?.click()} className="w-9 h-9 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-xl flex items-center justify-center shrink-0" title="Joindre une image"><Image className="w-3.5 h-3.5" /></button>
                  {recording ? (
                    <button onClick={() => { (window as any).__mr?.stop(); setRecording(false); }} className="w-9 h-9 bg-red-500 hover:bg-red-600 text-white rounded-xl flex items-center justify-center shrink-0 animate-pulse"><Square className="w-3.5 h-3.5" /></button>
                  ) : (
                    <button onClick={startRec} className="w-9 h-9 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-xl flex items-center justify-center shrink-0"><Mic className="w-3.5 h-3.5" /></button>
                  )}
                  <textarea
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    placeholder={`Écrire un message pour ${activeTeam.name}...`}
                    rows={2}
                    className="flex-1 bg-[#1A1D23] border border-[#2D3139] rounded-2xl px-4 py-3 text-white text-xs placeholder-slate-600 focus:border-indigo-500 focus:outline-none resize-none transition-colors"
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  />
                  <button onClick={handleSend} disabled={!content.trim() && !attachedImage && !audioBlob}
                    className="w-12 h-12 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-2xl transition-all flex items-center justify-center shadow-lg shadow-indigo-600/20 active:scale-95 shrink-0"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-[9px] text-slate-500 text-center">Appuyez sur Entrée pour envoyer</p>
              </div>
            </div>
          );
        })()}
      </div>
    </>
  );
}
